import { Badge } from "@/components/ui/Badge";
import { Table, Td, Th } from "@/components/ui/Table";
import { useReference } from "@/hooks/useReference";
import { cellTone, formatCell, type Matrix } from "@/lib/analysis";
import { cn } from "@/lib/utils";

/**
 * The antibiogram matrix (AN-06 … AN-08).
 *
 * Organisms as rows, antibiotics as columns, each cell the share susceptible.
 * A cell with too few isolates shows its count in brackets instead of a
 * percentage: "12 isolates, too few to report a percentage" is more useful to
 * a prescriber than an empty box, and far more honest than a percentage
 * computed from twelve results.
 */
export function AntibiogramTable({ matrix }: { matrix: Matrix }) {
  const { organismName, antibiotics } = useReference();

  const drugLabel = (code: string) =>
    antibiotics.find((a) => a.code === code)?.name ?? code;

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        Percent susceptible. A bracketed number is the isolate count where there
        were fewer than {matrix.params.suppression_threshold} results — too few
        to report a percentage.
        {matrix.params.numerator === "s_and_i" && (
          <>
            {" "}
            Intermediate results are counted as susceptible in this report.
          </>
        )}
      </p>

      <Table caption="Cumulative antibiogram: percent susceptible by organism and antibiotic">
        <thead>
          <tr>
            <Th className="left-0 z-20">Organism</Th>
            <Th className="text-right">Isolates</Th>
            {matrix.antibiotics.map((code) => (
              <Th key={code} className="text-center">
                <abbr title={drugLabel(code)} className="no-underline">
                  {code}
                </abbr>
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.organisms.map((code) => {
            const row = matrix.rows[code];
            if (!row) return null;
            return (
              <tr key={code} className="hover:bg-slate-50">
                <Td className="whitespace-nowrap italic">{organismName(code)}</Td>
                <Td className="text-right tabular-nums font-medium">{row.n_isolates}</Td>
                {matrix.antibiotics.map((drug) => {
                  const cell = row.cells[drug];
                  const tone = cellTone(cell);
                  const text = formatCell(cell);
                  return (
                    <Td
                      key={drug}
                      className={cn(
                        "text-center tabular-nums",
                        tone === "low" && "bg-red-50 text-red-900 font-medium",
                        tone === "mid" && "bg-amber-50 text-amber-900",
                        tone === "high" && "bg-green-50 text-green-900",
                        cell?.suppressed && "text-slate-500 italic",
                      )}
                      // A screen reader gets the full picture, not just "82%".
                      title={
                        cell && cell.n_tested > 0
                          ? `${drugLabel(drug)}: S ${cell.n_s}, I ${cell.n_i}, R ${cell.n_r} of ${cell.n_tested} tested${
                              cell.suppressed ? " — too few to report a percentage" : ""
                            }`
                          : `${drugLabel(drug)}: not tested`
                      }
                    >
                      {text || <span className="text-slate-300">&mdash;</span>}
                    </Td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>

      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
        <span className="font-medium">Shading:</span>
        <Badge tone="blocking">under 50% susceptible</Badge>
        <Badge tone="warning">50–79%</Badge>
        <Badge tone="ok">80% and above</Badge>
      </div>
    </div>
  );
}
